import React, { useCallback, useEffect, useReducer, useRef } from "react";
import { useParams } from "react-router-dom";
import { answerLiveSession, getPublicState, joinLiveSession } from "../../services/liveSessionApi";

// Public phone view (Q7-T1 §4.3, route /live/:shortCode) — anonymous, no login wall.
// join → current question → tap answer → locked-in → reveal → end-of-session
// conversion prompt. The participantId is held in sessionStorage for the session's
// lifetime ONLY and cleared on session end (REVIEW-SCOPE: session-scoped state with
// a stated lifecycle — no naked persistent client state). Correctness is
// server-computed; the phone never self-reports it. Polls every ~2.5s with jitter
// and the stateVersion short-circuit; backs off on error.
//
// S6 EPOCH RE-ARCHITECTURE (contract qlearn-q7-live-phone-epoch-rearchitecture-s6-2026-07-17,
// disposition rr-742bd2252f7f §6.1). The prior ad-hoc ref/effect concurrency model is
// replaced WHOLESALE by two layered mechanisms so that no artifact of a dead identity
// epoch can ever commit into a live one:
//   1. Remount boundary — the route renders <LivePhone key={shortCode}/> (routes.jsx),
//      so ANY shortCode change unmounts the old instance and mounts a fresh one; React
//      guarantees zero state/refs/reducer contents survive the key change. An in-place
//      param change (React Router 5 reusing the component) is additionally absorbed by
//      the reducer's identity-reset epoch bump below, so correctness never relies on the
//      key alone.
//   2. Single epoch-guarded reducer state machine — ALL live-phone state lives in one
//      useReducer carrying a monotonic integer `epoch`. Every async operation (join,
//      poll, answer) captures the epoch at launch and delivers its result ONLY as an
//      epoch-carrying dispatch; the reducer's FIRST clause discards any action whose
//      epoch is stale. Stale completions, rejections, and errors therefore cannot commit.
//      An AbortController per epoch (aborted on epoch bump and unmount) additionally
//      short-circuits each in-flight promise, but correctness never depends on abort
//      delivery — the epoch check alone is sufficient.
const POLL_BASE_MS = 2500;
const POLL_JITTER = 0.2;
const RETRY_CAP_MS = 30000;

// Deterministic per-client jitter derived from the server-issued participantId —
// NOT security-sensitive, uses no PRNG (so it never trips the Math.random security
// rule). Each phone gets a STABLE offset in [1-POLL_JITTER, 1+POLL_JITTER], which
// spreads 30 clients' polls without any client-side randomness.
function stableJitterFactor(seed) {
    const s = seed || "";
    let hash = 0;
    for (let i = 0; i < s.length; i += 1) {
        hash = (hash * 31 + s.charCodeAt(i)) & 0xffff;
    }
    const unit = (hash / 0xffff) * 2 - 1; // [-1, 1]
    return 1 + unit * POLL_JITTER;
}

function pollDelay(errorCount, jitterFactor) {
    const backoff = Math.min(errorCount, 4);
    const base = POLL_BASE_MS * Math.pow(1.6, backoff);
    return base * jitterFactor;
}

// Bounded backoff for the reachable join-retry transition (ERROR_RETRYABLE).
function retryDelay(attempt) {
    const n = Math.min(attempt, 4);
    return Math.min(POLL_BASE_MS * Math.pow(2, n), RETRY_CAP_MS);
}

// An opaque server-issued id is valid ONLY as a non-empty, non-whitespace string.
// Truthiness is NOT validation (R2): an object/number/array/boolean is a malformed id
// that must never reach the wire or the charCodeAt-based jitter seed.
function isNonEmptyString(value) {
    return typeof value === "string" && value.trim().length > 0;
}

function keyFor(shortCode) {
    return `mls_live_participant_${shortCode}`;
}

// B2 cure: recover a COMPLETE, session-scoped identity {sessionId, participantId}
// bound to THIS shortCode. A pair is trusted ONLY when the stored blob is a plain
// object, its shortCode matches, and BOTH ids are non-empty strings. Anything else —
// malformed JSON, non-object (number/array/boolean/null), partial, whitespace-only, or
// cross-shortCode — is cleared and treated as "not joined" so the phone rejoins cleanly
// rather than emitting a malformed or missing id (backend @NotBlank -> 400). The stored
// value is opaque server-issued state only — never a credential, never client identity.
// (R2 validation predicate — preserved verbatim as invariant I4.)
function loadIdentity(shortCode, storageKey) {
    let raw = null;
    try {
        raw = window.sessionStorage.getItem(storageKey);
    } catch (_) {
        return { sessionId: null, participantId: null };
    }
    if (!raw) {
        return { sessionId: null, participantId: null };
    }
    try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)
                && parsed.shortCode === shortCode
                && isNonEmptyString(parsed.sessionId) && isNonEmptyString(parsed.participantId)) {
            return { sessionId: parsed.sessionId, participantId: parsed.participantId };
        }
    } catch (_) {
        // fall through to clear
    }
    // malformed / partial / cross-shortCode / wrong-typed: fail closed.
    try {
        window.sessionStorage.removeItem(storageKey);
    } catch (_) {
        // clearing must never break the phone
    }
    return { sessionId: null, participantId: null };
}

// ---- Machine phases (disposition §6.1) ----
const PHASE = {
    BOOT: "BOOT",
    JOINING: "JOINING",
    LIVE: "LIVE",
    ANSWERING: "ANSWERING",
    ERROR_RETRYABLE: "ERROR_RETRYABLE",
    TERMINAL: "TERMINAL",
};

// Lazy reducer init — sessionStorage is read EXACTLY once per mount (I4). A valid
// cached pair starts LIVE (no join); anything else starts JOINING.
function makeInitialState(shortCode) {
    const cached = loadIdentity(shortCode, keyFor(shortCode));
    const hasIdentity = isNonEmptyString(cached.sessionId) && isNonEmptyString(cached.participantId);
    return {
        epoch: 0,
        shortCode,
        phase: hasIdentity ? PHASE.LIVE : PHASE.JOINING,
        sessionId: hasIdentity ? cached.sessionId : null,
        participantId: hasIdentity ? cached.participantId : null,
        state: null,
        choice: null,
        error: "",
        version: null,
        retryCount: 0,
    };
}

// The single serialization point. Its FIRST clause discards any epoch-carrying action
// from a dead epoch, so no stale join/poll/answer completion, rejection, or error can
// commit. Epoch is incremented by exactly two transition classes: identity reset
// (RESET_IDENTITY on an in-place shortCode change, RECONNECT on a malformed-identity
// answer) and unmount-abort (handled by the AbortController, not a dispatch).
function reducer(state, action) {
    // I2/§6.1: stale epochs are dropped before anything else.
    if (action.epoch !== undefined && action.epoch !== state.epoch) {
        return state;
    }
    switch (action.type) {
        case "RESET_IDENTITY": {
            // Identity reset (epoch bump): an in-place shortCode change reached the
            // reducer. Load the NEW code's own namespaced identity (or nothing -> join).
            const cached = action.cached;
            const hasIdentity = isNonEmptyString(cached.sessionId) && isNonEmptyString(cached.participantId);
            return {
                epoch: state.epoch + 1,
                shortCode: action.shortCode,
                phase: hasIdentity ? PHASE.LIVE : PHASE.JOINING,
                sessionId: hasIdentity ? cached.sessionId : null,
                participantId: hasIdentity ? cached.participantId : null,
                state: null,
                choice: null,
                error: "",
                version: null,
                retryCount: 0,
            };
        }
        case "RECONNECT": {
            // Identity reset (epoch bump): a stale/partial/malformed identity was found
            // at answer time — clear and rejoin rather than sending an invalid answer.
            return {
                ...state,
                epoch: state.epoch + 1,
                phase: PHASE.JOINING,
                sessionId: null,
                participantId: null,
                state: null,
                choice: null,
                error: "Reconnecting to the session…",
                version: null,
                retryCount: 0,
            };
        }
        case "JOIN_SUCCESS":
            return {
                ...state,
                phase: PHASE.LIVE,
                sessionId: action.sessionId,
                participantId: action.participantId,
                error: "",
            };
        case "JOIN_FAILURE":
            if (action.status === 410) {
                return { ...state, phase: PHASE.TERMINAL, error: "This session has ended." };
            }
            return {
                ...state,
                phase: PHASE.ERROR_RETRYABLE,
                error: "Could not join this session.",
                retryCount: state.retryCount + 1,
            };
        case "RETRY_JOIN":
            // Reachable retry transition (same epoch) — a machine transition, not a ref
            // reset hoping an effect notices.
            return { ...state, phase: PHASE.JOINING, error: "" };
        case "POLL_SUCCESS": {
            const next = action.next;
            if (next.unchanged) {
                return state.error || state.retryCount ? { ...state, error: "", retryCount: 0 } : state;
            }
            const prevIndex = state.state && state.state.currentQuestionIndex;
            const questionChanged = next.currentQuestionIndex != null && next.currentQuestionIndex !== prevIndex;
            return {
                ...state,
                state: next,
                version: next.stateVersion != null ? next.stateVersion : state.version,
                sessionId: isNonEmptyString(next.sessionId) ? next.sessionId : state.sessionId,
                choice: questionChanged ? null : state.choice,
                error: "",
                retryCount: 0,
            };
        }
        case "POLL_FAILURE":
            return { ...state, retryCount: state.retryCount + 1 };
        case "ANSWER_START":
            return { ...state, phase: PHASE.ANSWERING, choice: action.choice, error: "" };
        case "ANSWER_SUCCESS":
            return state.phase === PHASE.ANSWERING ? { ...state, phase: PHASE.LIVE } : state;
        case "ANSWER_FAILURE":
            return { ...state, phase: PHASE.LIVE, error: "Could not record your answer." };
        default:
            return state;
    }
}

export default function LivePhone() {
    const { shortCode } = useParams();
    const [machine, dispatch] = useReducer(reducer, shortCode, makeInitialState);
    const { epoch, phase, sessionId, participantId, state, choice, error, version } = machine;

    // I1: the ONLY refs are the AbortController handle (one per epoch). No identity,
    // question, choice, error, or poll bookkeeping ever lives in a ref or useState.
    const controllerRef = useRef(null);

    const storageKey = keyFor(shortCode);
    const synced = machine.shortCode === shortCode;
    const pollingActive = synced && !!participantId && (phase === PHASE.LIVE || phase === PHASE.ANSWERING);

    // --- Controller lifecycle: one AbortController per epoch, aborted on epoch bump and
    // on unmount (I5). Async ops capture it at launch and refuse to dispatch once aborted
    // — this is the belt to the reducer epoch-guard's suspenders. Defined FIRST so it is
    // set before the join/poll effects for this epoch read it, and its cleanup (the last
    // to run on an epoch change) aborts the epoch the in-flight ops captured. The frozen
    // API layer takes no signal, so abort does not cancel the network — correctness rests
    // on the epoch check (§5.1); the abort flag is the in-component liveness gate that
    // makes a post-unmount/post-epoch dispatch impossible to observe.
    useEffect(() => {
        const controller = new AbortController();
        controllerRef.current = controller;
        return () => controller.abort();
    }, [epoch]);

    // --- In-place shortCode change (React Router 5 param navigation reusing the same
    // component instance, no remount): dispatch an identity reset so the reducer bumps
    // the epoch and rebinds to the new code. On a real remount (key={shortCode}) this
    // never fires because the fresh mount's lazy init already matches. I3: the reset is
    // performed by the reducer, never during render.
    useEffect(() => {
        if (machine.shortCode !== shortCode) {
            dispatch({ type: "RESET_IDENTITY", shortCode, cached: loadIdentity(shortCode, keyFor(shortCode)) });
        }
    }, [shortCode, machine.shortCode]);

    // --- Persist a COMPLETE identity for same-code refresh recovery. Written only when
    // we hold both non-empty string ids for the CURRENT synced code (I4: named
    // transition; never a render-time write). Idempotent, so re-runs are harmless.
    useEffect(() => {
        if (synced && isNonEmptyString(sessionId) && isNonEmptyString(participantId)) {
            try {
                window.sessionStorage.setItem(storageKey, JSON.stringify({ shortCode, sessionId, participantId }));
            } catch (_) {
                // persistence is best-effort; failure never breaks the phone.
            }
        }
    }, [synced, sessionId, participantId, shortCode, storageKey]);

    // --- Session-scoped lifecycle: clear the stored identity once the session has ENDED.
    useEffect(() => {
        if (synced && state && state.state === "ENDED") {
            try {
                window.sessionStorage.removeItem(storageKey);
            } catch (_) {
                // clearing must never break the phone.
            }
        }
    }, [synced, state, storageKey]);

    // --- Join driver: fires only when the reducer is synced to the param and the machine
    // is in JOINING (fresh mount without cache, an in-place reset to a new code, a
    // RECONNECT, or a RETRY_JOIN). The result is epoch-guarded twice: the captured
    // controller's abort short-circuits it, and the reducer discards a stale epoch.
    useEffect(() => {
        if (!synced || phase !== PHASE.JOINING) {
            return;
        }
        const controller = controllerRef.current;
        const myEpoch = epoch;
        (async () => {
            try {
                const resp = await joinLiveSession(shortCode);
                if (controller.signal.aborted) return;
                dispatch({ type: "JOIN_SUCCESS", epoch: myEpoch, sessionId: resp.sessionId, participantId: resp.participantId });
            } catch (e) {
                if (controller.signal.aborted) return;
                dispatch({ type: "JOIN_FAILURE", epoch: myEpoch, status: e && e.status });
            }
        })();
    }, [synced, phase, epoch, shortCode]);

    // --- Reachable retry: ERROR_RETRYABLE schedules a bounded-backoff timer whose firing
    // dispatches RETRY_JOIN (same epoch) -> JOINING -> the join driver re-runs. The user
    // control below dispatches the identical action. Retry is a machine transition, so it
    // is reachable by construction (defends the "retry never occurs" defect).
    useEffect(() => {
        if (phase !== PHASE.ERROR_RETRYABLE) {
            return undefined;
        }
        const myEpoch = epoch;
        const timer = setTimeout(() => dispatch({ type: "RETRY_JOIN", epoch: myEpoch }), retryDelay(machine.retryCount));
        return () => clearTimeout(timer);
    }, [phase, epoch, machine.retryCount]);

    // --- Poll loop: a single self-scheduling loop per epoch. It tracks the stateVersion
    // cursor in a LOCAL variable (not a ref — I1) because it is the sole poller within the
    // epoch; the reducer also holds `version` for render/state and to seed a fresh loop.
    // Every completion is epoch-guarded (captured controller + reducer). The loop stops
    // rescheduling the instant its controller aborts, so no stale-code poll ever fires
    // after an epoch bump or unmount.
    useEffect(() => {
        if (!pollingActive) {
            return undefined;
        }
        const controller = controllerRef.current;
        const myEpoch = epoch;
        const jitter = stableJitterFactor(participantId);
        let localVersion = version;
        let localErrors = 0;
        let timer = null;
        const tick = async () => {
            try {
                const next = await getPublicState(shortCode, localVersion);
                if (controller.signal.aborted) return;
                if (!next.unchanged && next.stateVersion != null) {
                    localVersion = next.stateVersion;
                }
                localErrors = 0;
                dispatch({ type: "POLL_SUCCESS", epoch: myEpoch, next });
            } catch (e) {
                if (controller.signal.aborted) return;
                localErrors += 1;
                dispatch({ type: "POLL_FAILURE", epoch: myEpoch });
            }
            if (controller.signal.aborted) return;
            timer = setTimeout(tick, pollDelay(localErrors, jitter));
        };
        tick();
        return () => { if (timer) clearTimeout(timer); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pollingActive, epoch, participantId, shortCode]);

    const submit = useCallback((opt) => {
        if (!state || state.state !== "QUESTION" || !state.question) return;
        // Fail closed: never emit an answer without BOTH server-issued NON-EMPTY STRING
        // ids (truthiness is not validation — R2). A stale, partial, or malformed identity
        // is cleared and the phone rejoins (RECONNECT = identity reset) rather than
        // sending an invalid answer.
        if (!isNonEmptyString(sessionId) || !isNonEmptyString(participantId)) {
            try { window.sessionStorage.removeItem(storageKey); } catch (_) { /* noop */ }
            dispatch({ type: "RECONNECT" });
            return;
        }
        const controller = controllerRef.current;
        const myEpoch = epoch;
        const questionId = state.question.id;
        dispatch({ type: "ANSWER_START", epoch: myEpoch, choice: opt });
        (async () => {
            try {
                await answerLiveSession({ sessionId, participantId, questionId, choice: opt });
                if (controller.signal.aborted) return;
                dispatch({ type: "ANSWER_SUCCESS", epoch: myEpoch });
            } catch (e) {
                if (controller.signal.aborted) return;
                dispatch({ type: "ANSWER_FAILURE", epoch: myEpoch });
            }
        })();
    }, [state, sessionId, participantId, epoch, storageKey]);

    if (phase === PHASE.ERROR_RETRYABLE && !state) {
        return (
            <div style={{ padding: 24 }}>
                {error || "Could not join this session."}{" "}
                <button type="button" onClick={() => dispatch({ type: "RETRY_JOIN", epoch })}>Retry</button>
            </div>
        );
    }
    if (error && !state) {
        return <div style={{ padding: 24 }}>{error}</div>;
    }
    if (!state) {
        return <div style={{ padding: 24 }}>Joining…</div>;
    }

    const wrap = { maxWidth: 480, margin: "0 auto", padding: 20, fontFamily: "system-ui, sans-serif" };

    if (state.state === "ENDED") {
        const url = state.ended && state.ended.conversionUrl ? state.ended.conversionUrl : "/register";
        return (
            <div style={wrap}>
                <h1>That's a wrap!</h1>
                <p>Save your results and keep practicing — create a free QLearn account.</p>
                <a href={url} style={{ display: "inline-block", padding: "12px 20px", background: "#137333", color: "#fff", borderRadius: 6, textDecoration: "none" }}>
                    Create a free account
                </a>
            </div>
        );
    }

    if (state.state === "LOBBY" || !state.question) {
        return <div style={wrap}><h2>You're in! Waiting for the host to start…</h2></div>;
    }

    const revealed = state.state === "REVEAL" && state.reveal;
    return (
        <div style={wrap}>
            <h2>{state.question.stem}</h2>
            {(state.question.options || []).map((opt) => {
                const isChosen = choice === opt;
                const isCorrect = revealed && state.reveal.correctOption === opt;
                let bg = "#fff";
                if (revealed && isCorrect) bg = "#e6f4ea";
                else if (revealed && isChosen && !isCorrect) bg = "#fce8e6";
                else if (isChosen) bg = "#e8f0fe";
                return (
                    <button key={opt} onClick={() => submit(opt)} disabled={state.state !== "QUESTION" || choice != null}
                            style={{ display: "block", width: "100%", textAlign: "left", padding: 14, margin: "8px 0", background: bg, border: "1px solid #ccc", borderRadius: 8 }}>
                        {opt} {revealed && isCorrect ? "✓" : ""}
                    </button>
                );
            })}
            {state.state === "QUESTION" && choice != null && <p style={{ color: "#137333" }}>Answer locked in.</p>}
            {revealed && (
                <p>{choice === state.reveal.correctOption ? "Correct!" : "Not this time."}</p>
            )}
        </div>
    );
}
